import { AUTH_DROPOFF_MS, RegistrationStage, SESSION_TTL_MS, STORAGE_KEY_PREFIX } from './constants';

/**
 * @description
 * The last problem the user ran into, used to explain an exit (exit_reason)
 */
export type Issue = { reason: string; at: number };

/**
 * @description
 * The cart at the moment the backend accepted the submit. Redirect-based payments (Klarna, Vipps,
 * mobile Swish, ePassi) reload the page with an empty form, so purchase values come from here.
 */
export type CartSnapshot = {
  value: number;
  currency: string | undefined;
  regType: string;
  paymentMethod: string;
  participantsCount: number;
  priceBand: string;
  items: Array<{ item_id: string; item_category: string; quantity: number }>;
};

export type AnalyticsSession = {
  checkoutId: string;
  raceId: number;
  startedAt: number;
  updatedAt: number;
  stage: RegistrationStage;
  stageAt: number;
  viewedSent: boolean;
  lastSection: string | null;
  formStarted: boolean;
  sectionsStarted: string[];
  blockedSent: string[];
  issue: Issue | null;
  authPending: { mode: string; at: number } | null;
  registrationUuid: string | null;
  cart: CartSnapshot | null;
  completed: boolean;
  abandoned: boolean;
};

export function storageKey(raceId: number): string {
  return `${STORAGE_KEY_PREFIX}${raceId}`;
}

function randomId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch (e) {
    // fall through
  }

  return `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

export function createSession(raceId: number, now: number): AnalyticsSession {
  return {
    checkoutId: randomId(),
    raceId,
    startedAt: now,
    updatedAt: now,
    stage: 'viewed',
    stageAt: now,
    viewedSent: false,
    lastSection: null,
    formStarted: false,
    sectionsStarted: [],
    blockedSent: [],
    issue: null,
    authPending: null,
    registrationUuid: null,
    cart: null,
    completed: false,
    abandoned: false,
  };
}

export function readSession(storage: Storage | null, raceId: number): AnalyticsSession | null {
  try {
    const raw = storage?.getItem(storageKey(raceId));
    return raw ? (JSON.parse(raw) as AnalyticsSession) : null;
  } catch (e) {
    return null;
  }
}

export function writeSession(storage: Storage | null, session: AnalyticsSession) {
  try {
    storage?.setItem(storageKey(session.raceId), JSON.stringify(session));
  } catch (e) {
    // storage full or blocked, analytics must never break the flow
  }
}

export function removeSession(storage: Storage | null, raceId: number) {
  try {
    storage?.removeItem(storageKey(raceId));
  } catch (e) {
    // see writeSession
  }
}

const COMPLETED_KEY = `${STORAGE_KEY_PREFIX}completed`;

/**
 * @description
 * Registrations already reported as completed in this tab,
 * so a reload of the success page (e.g. ?registration_complete=<uuid>) does not report them again
 */
export function isCompletedRegistration(storage: Storage | null, registrationUuid: string): boolean {
  try {
    const list: string[] = JSON.parse(storage?.getItem(COMPLETED_KEY) || '[]');
    return list.includes(registrationUuid);
  } catch (e) {
    return false;
  }
}

export function markCompletedRegistration(storage: Storage | null, registrationUuid: string) {
  try {
    const list: string[] = JSON.parse(storage?.getItem(COMPLETED_KEY) || '[]');
    storage?.setItem(COMPLETED_KEY, JSON.stringify([...list, registrationUuid].slice(-20)));
  } catch (e) {
    // see writeSession
  }
}

/**
 * @description
 * Decide what to do with a stored session when the registration page opens:
 * - resume: same checkout continues (reload, Odin or payment redirect return)
 * - authDropoff: the user went to Odin and never came back in time, report it and start over
 * - expired: too old or already completed, start over
 */
export function classifyStoredSession(stored: AnalyticsSession | null, now: number): 'none' | 'resume' | 'authDropoff' | 'expired' {
  if (!stored) return 'none';
  if (stored.completed) return 'expired';
  if (stored.authPending && now - stored.authPending.at > AUTH_DROPOFF_MS) return 'authDropoff';
  if (now - stored.updatedAt > SESSION_TTL_MS) return 'expired';
  return 'resume';
}

/**
 * @description
 * Why did the user leave, based on the most recent problem in the session
 */
export function exitReason(session: AnalyticsSession): string {
  if (session.authPending) return 'auth_dropoff';
  return session.issue?.reason || 'no_error';
}
